import React from 'react'
import axios, { AxiosError, CancelTokenSource, ResponseType } from 'axios'
import { Toasts } from './providers/toast'
import { Popups } from './providers/popups'

const requestCache: Record<string, any> = {}

/**
 * Make a request to the given URL with the given data and method.
 *
 * @param url The URL to make the request to.
 * @param data The data to send with the request.
 * @param method The HTTP method to use for the request. Defaults to 'POST'.
 * @param responseType The type of response to expect. Defaults to 'json'.
 * @returns A promise that resolves to the response data. If the request fails, the promise fails with an AxiosError. See showAxiosErrorAsToast for default error handling.
 * We do not handle errors in this request, because we want to force users to consider the error case themselves.
 */
export function makeRequest<T>(
  url: string,
  data: any = undefined,
  method: string = 'POST',
  responseType?: ResponseType
) {
  return axios<T>({
    url,
    method,
    data,
    responseType,
  }).then((r) => r.data)
}

/** Make a request that is guarenteed to run by itself. This is good for things that change the session or user.
 * see axiosQueue.ts for more details.
 */
export function makeExclusiveRequest<T>(
  url: string,
  data: any = undefined,
  method: string = 'POST',
  responseType?: ResponseType
) {
  // @ts-ignore
  const result = axios<T>({
    url,
    method,
    data,
    responseType,
    exclusive: true,
  }).then((r) => r.data)
  // fix the ts ignore above return type
  return result as unknown as Promise<T>
}

/**
 * Make a request to the given URL with the given data and method, and return the response as a Blob.
 */
export function requestBlob(url: string, data: any = undefined) {
  return makeRequest<Blob>(url, data, 'POST', 'blob')
}

/**
 * Hook that returns a function to make requests while the component is mounted. On unmount, all requests are cancelled.
 * @returns A function to make requests that returns a promise that resolves to the response data.
 */
export const useRequestsWhileMounted = () => {
  const abortTokens = React.useRef<CancelTokenSource[]>([])
  React.useEffect(() => {
    abortTokens.current = []
    return () => {
      abortTokens.current.forEach((token) => token.cancel(REQUEST_ABORTED))
    }
  }, [])
  return <T,>(url: string, data: any = undefined, method: string = 'POST') => {
    const abortSource = axios.CancelToken.source()
    abortTokens.current.push(abortSource)
    return axios<T>({
      url,
      method,
      data,
      cancelToken: abortSource.token,
    }).then((r) => {
      abortTokens.current = abortTokens.current.filter((t) => t !== abortSource)
      return r.data
    })
  }
}

/**
 * Make a request to the given URL with the given data and method, and return undefned if the request fails.
 * A toast is shown with the error message.
 * @param url The URL to make the request to.
 * @param data The data to send with the request.
 * @param method The HTTP method to use for the request. Defaults to 'POST'.
 * @returns A promise that resolves to the response data. If the request fails, the promise resolves to undefined.
 */
export function requestOrUndefined<T>(url: string, data: any = undefined, method: string = 'POST') {
  return axios<T>({
    url,
    method,
    data,
  })
    .then((response) => response.data)
    .catch((error) => {
      showAxiosErrorAsToast(error)
      return undefined
    })
}

export const REQUEST_ABORTED = 'Request aborted'
export const wasCancelled = (error: AxiosError) => error.message === REQUEST_ABORTED // axios.isCancel(error) doesnt work

let slowDownToastTime: Date | undefined = undefined
const slowDownToastDebounced = () => {
  if (slowDownToastTime && new Date().getTime() - slowDownToastTime.getTime() < 1000) return
  slowDownToastTime = new Date()
  Toasts.showToast({ subject: 'Please Slow Down!', message: 'Please wait a bit and try again', error: true })
}

const standardRequestFailure = (error: AxiosError) => {
  const status = error.response?.status
  if (wasCancelled(error)) {
    // Do nothing
  } else if (429 === status) {
    slowDownToastDebounced()
  } else if (419 === status) {
    Popups.confirm({
      title: <span className="text-xl font-bold">Tab Session Expired</span>,
      message: (
        <span>
          Please{' '}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault()
              window.location.reload()
            }}
          >
            refresh
          </a>{' '}
          the tab to reconnect.
        </span>
      ),
      closeable: true,
    })
  } else if (504 === status) {
    Toasts.showToast({ message: 'Request timed out', error: true, duration: 99999999 })
  } else {
    return false
  }
  return true
}

/**
 * A standard error handler for axios requests.
 * This function checks if the error is an AxiosError and if it is, it shows a toast with the error message.
 * If the error is not an AxiosError, it throws the error again.
 */
export const showAxiosErrorAsToast = (error: unknown, overwriteMessage?: string) => {
  if (!(error instanceof Error)) throw error
  if (!axios.isAxiosError(error)) throw error
  if (!standardRequestFailure(error)) Toasts.showToast({ message: overwriteMessage || error.message, error: true })
}

export const laravelValidationToToast = (title: string, e: unknown) => {
  try {
    if (!axios.isAxiosError(e)) throw e
    Toasts.showToast({
      subject: title,
      message: Object.values((e.response?.data as any).errors).join(', '),
    })
  } catch (_) {
    showAxiosErrorAsToast(e)
  }
}

/**
 * A hook that makes a request to the given URL with the given data and method.
 * The request is made when the component mounts and when the postData or activate parameters change.
 * Make sure postData is stable when using this hook, otherwise the request will be made every time the component re-renders.
 * @param url The URL to make the request to.
 * @param postData The data to send with the request. Defaults to undefined. Make sure this is stable, otherwise the request will be made every time the component re-renders.
 * @param activate Whether to make the request or not. Defaults to true. If false, the request will not be made.
 * @param method The HTTP method to use for the request. Defaults to 'POST'.
 * @returns
 * - `data`: The response data from the request. If the request fails, this will be undefined.
 * - `loading`: Whether the request is still loading or not.
 * - `reload`: A function to reload the request. This will make the request again with the current postData and activate parameters.
 */
export function useRequest<T>(
  url: string,
  postData: any = undefined,
  activate: boolean = true,
  method: string = 'POST'
) {
  const [loading, setLoading] = React.useState(true)
  const [data, setData] = React.useState<T | undefined>(undefined)
  const abortSource = React.useRef<CancelTokenSource | undefined>(undefined)

  const reload = React.useCallback(async () => {
    if (!activate) {
      setLoading(false)
      return
    }
    setLoading(true)
    if (abortSource.current) abortSource.current.cancel(REQUEST_ABORTED)
    abortSource.current = axios.CancelToken.source()
    try {
      const response = await axios<T>({
        url,
        method,
        data: postData,
        cancelToken: abortSource.current.token,
      })
      setData(response.data)
    } catch (error) {
      setData(undefined)
      showAxiosErrorAsToast(error)
    } finally {
      setLoading(false)
      abortSource.current = undefined
    }
  }, [url, postData, activate, method])

  React.useEffect(() => {
    reload()
  }, [postData, activate])

  React.useEffect(() => {
    return () => abortSource.current && abortSource.current.cancel(REQUEST_ABORTED)
  }, [])

  return { data, loading, reload }
}

/**
 * A wrapper around useRequest that globally caches the result of the request.
 * @param url The URL to make the request to.
 * @param extraKey An optional key to use for caching. This is useful if you want to cache multiple requests to the same URL with different data.
 * @param method The HTTP method to use for the request. Defaults to 'POST'.
 *
 * See useRequest for more details.
 */
export const useCachedRequest = <T,>(url: string, extraKey?: string | number, method: string = 'POST') => {
  const cacheKey = `${url}-${extraKey}`
  const cachedResult = requestCache[cacheKey] as T | undefined
  const { data, loading, reload } = useRequest<T>(url, undefined, !cachedResult, method)
  React.useEffect(() => {
    if (data) requestCache[cacheKey] = data
  }, [data, url])
  const reloadWrapper = React.useCallback(() => {
    delete requestCache[cacheKey]
    reload()
  }, [reload, url])
  return { data: cachedResult || data, loading, reload: reloadWrapper }
}

/**
 * A hook that makes a paginated request to the given URL with the given data and method.
 * This hook handles adding additional pages to the results and loading more data when needed.
 * Similar to useRequest, the request is made when the component mounts and when the postData or activate parameters change.
 * Make sure postData is stable when using this hook, otherwise the request will be made every time the component re-renders.
 * @param url The URL to make the request to.
 * @param postData The data to send with the request. Defaults to undefined. Make sure this is stable, otherwise the request will be made every time the component re-renders.
 * @param onDataLoaded A callback function that is called whenever partial data is loaded.
 *
 * @returns
 * - `values`: The response data from the request. If the request fails, this will be undefined.
 * - `loading`: Whether the request is still loading or not. When 'loading more', this is always false.
 * - `loadingMore`: Whether any data is being loaded or not. Unlike 'loading', this is true when loading more data.
 * - `loadMore`: A function to load more data. This will make the request again but load the next page of data.
 * - `reload`: A function to reload the request. This will make the request again with the current postData and page 0.
 * - `stale`: An object that indicates if the current data is stale. If the postData has changed since the last request, this will be an object with the current and old postData. If the data is not stale, this will be undefined.
 */
export const usePaginatedData = <T, P>(url: string, postData: P, onDataLoaded: () => void = () => {}) => {
  // This hook's state looks a little weird. This is because the straightforward way
  // of doing it allows for short moments where parts of the state are out of sync.

  // post data is tracked so that if the post data changes, old results are indicated through 'stale'
  // this state follows the path of a request so that latestCompletedPostData is always
  // the post data that yielded the current values, allowing us to check if its stale.
  const [latestRequestedPostData, setLatestRequestedPostData] = React.useState(postData)
  const [latestCompletedPostData, setLatestCompletedPostData] = React.useState<P | undefined>(undefined)

  const [fullInputData, setFullInputData] = React.useState({ ...postData, page: 0 })
  const page = fullInputData.page
  const [fullOutputData, setFullOutputData] = React.useState<{
    values: T[]
    more: boolean
    loading: boolean
    loadingMore: boolean
  }>({ values: [], more: false, loading: true, loadingMore: true })

  const loadPage = (page: number) => {
    setLatestRequestedPostData(postData)
    setFullInputData({ ...postData, page })
    setFullOutputData((o) => ({ ...o, loading: page === 0, loadingMore: true }))
  }

  React.useEffect(() => {
    if (latestRequestedPostData !== postData) loadPage(0)
  }, [postData])

  const { data } = useRequest<{
    page: number
    more: boolean
    values: T[]
  }>(url, fullInputData)

  React.useEffect(() => {
    if (!data) return
    setFullOutputData((old) => ({
      values: data.page === 0 ? data.values : data.page === page ? [...old.values, ...data.values] : [],
      more: data.more,
      loading: false,
      loadingMore: false,
    }))
    setLatestCompletedPostData(latestRequestedPostData)
    onDataLoaded()
  }, [data])

  return {
    values: fullOutputData.values,
    loading: fullOutputData.loading,
    loadingMore: fullOutputData.loadingMore,
    loadMore: fullOutputData.more ? () => loadPage(page + 1) : undefined,
    reload: () => loadPage(0),
    stale:
      postData !== latestCompletedPostData
        ? {
            current: postData,
            old: latestCompletedPostData,
          }
        : undefined,
  }
}
